import { motion } from "framer-motion";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useHousehold } from "@/hooks/useHousehold";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { trackMetaEvent } from "@/lib/metaPixel";
import { getErrorMessage } from "@/lib/errors";

type PlanKey =
  | "individual_monthly"
  | "individual_annual"
  | "family_monthly"
  | "family_annual";

type PlanCard = {
  key: PlanKey;
  name: string;
  price: string;
  cadence: string;
  tagline: string;
  features: string[];
  featured?: boolean;
  cta: string;
};

const PLANS: PlanCard[] = [
  {
    key: "individual_monthly",
    name: "HeirWise Individual",
    price: "$24.99",
    cadence: "/month",
    tagline: "1 teen",
    cta: "Get Individual",
    features: [
      "Adaptive lessons and decision practice",
      "Parent dashboard & progress insights",
      "1 teen profile",
      "One lesson per day",
    ],
  },
  {
    key: "individual_annual",
    name: "HeirWise Individual — Annual",
    price: "$249",
    cadence: "/year",
    tagline: "1 teen · save vs monthly",
    cta: "Get Individual Annual",
    features: [
      "Everything in Individual monthly",
      "Save vs paying monthly",
      "1 teen profile",
      "One lesson per day",
    ],
  },
  {
    key: "family_monthly",
    name: "HeirWise Family",
    price: "$39.99",
    cadence: "/month",
    tagline: "Up to 4 teens",
    cta: "Get Family",
    features: [
      "Adaptive lessons for every teen",
      "Parent dashboard across all teens",
      "Up to 4 teen profiles",
      "One lesson per day per teen",
    ],
  },
  {
    key: "family_annual",
    name: "HeirWise Family — Annual",
    price: "$399",
    cadence: "/year",
    tagline: "Up to 4 teens · best value",
    featured: true,
    cta: "Get Family Annual",
    features: [
      "Everything in Family monthly",
      "Best value for a whole household",
      "Up to 4 teen profiles",
      "One lesson per day per teen",
    ],
  },
];


const PricingSection = () => {
  const { user } = useAuth();
  const { household, createHousehold } = useHousehold();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState<PlanKey | null>(null);

  const handleSelectPlan = async (planKey: PlanKey) => {
    if (loadingPlan) return;
    if (!user) {
      navigate(`/login?mode=signup&plan=${encodeURIComponent(planKey)}`);
      return;
    }
    setLoadingPlan(planKey);
    void trackMetaEvent("checkout_started", {
      email: user.email ?? undefined,
      externalId: user.id,
      customData: { plan_key: planKey, surface: "landing_pricing" },
    });
    try {
      let hhId = household?.id;
      if (!hhId) {
        const created = await createHousehold("My Family");
        hhId = created?.id;
      }
      if (!hhId) throw new Error("Could not prepare your household.");
      const { data, error } = await supabase.functions.invoke("stripe-billing", {
        body: { action: "create_checkout", household_id: hhId, plan_key: planKey },
      });
      if (error) throw error;
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url;
    } catch (err: unknown) {
      toast({
        title: "Couldn't start checkout",
        description: getErrorMessage(err) || "Please try again.",
        variant: "destructive",
      });
      setLoadingPlan(null);
    }
  };

  return (
    <section id="pricing" className="py-24 bg-background scroll-mt-20">
      <div className="container mx-auto px-6">
        <motion.div
          className="max-w-3xl mx-auto text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-primary font-medium text-sm tracking-widest uppercase mb-3">Pricing</p>
          <h2 className="text-3xl md:text-4xl font-serif text-foreground mb-3">
            One teen or the whole household.
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Every plan includes adaptive lessons, decision practice, assessments, simulations, and parent progress insight. Cancel anytime.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">

          {PLANS.map((plan) => {
            const isLoading = loadingPlan === plan.key;
            return (
              <motion.div
                key={plan.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`relative rounded-3xl border bg-card/95 backdrop-blur-xl p-7 flex flex-col ${
                  plan.featured ? "border-primary/50 shadow-card-hover" : "border-border"
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold uppercase tracking-wider">
                    Best Value
                  </div>
                )}
                <h3 className="text-lg font-serif text-foreground">{plan.name}</h3>
                <p className="text-xs text-muted-foreground mt-1">{plan.tagline}</p>
                <div className="mt-4 mb-5">
                  <span className="font-heading font-bold text-4xl text-gradient-blue leading-none">{plan.price}</span>
                  <span className="text-muted-foreground text-sm ml-1">{plan.cadence}</span>
                </div>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <div className="w-4 h-4 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="h-2.5 w-2.5 text-primary" />
                      </div>
                      <span className="text-sm text-foreground/85 leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  size="lg"
                  onClick={() => handleSelectPlan(plan.key)}
                  disabled={Boolean(loadingPlan)}
                  className={`w-full ${plan.featured ? "bg-primary text-primary-foreground shadow-glow" : ""}`}
                  variant={plan.featured ? "default" : "outline"}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Preparing…
                    </span>
                  ) : (
                    <>
                      {plan.cta}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </>
                  )}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          Individual covers 1 teen. Family covers up to 4 teens. Both plans include the complete HeirWise learning experience. Cancel anytime.
        </p>

      </div>
    </section>
  );
};

export default PricingSection;